import { drawBanner, type DotColors } from '@bn-games/render-helpers';
import type { Color } from '../../game/types.js';
import { BOARD_OUTER_OFFSET, CANVAS_SIZE } from '../../utils/constants.js';
import type { RenderPalette } from '../palette.js';

/**
 * Minimal view model for the banner. The full phase machine (selecting,
 * chaining, ...) lands in Phase 4; for now Phase 2 only needs to show whose
 * turn it is and end-game text.
 */
export type TurnBannerView =
  | { readonly kind: 'turn'; readonly player: Color }
  | { readonly kind: 'win'; readonly winner: Color }
  | { readonly kind: 'draw' };

const BANNER_HEIGHT = 30;
const BANNER_RADIUS = 15;
const BANNER_FONT = 'bold 14px "Segoe UI", system-ui, sans-serif';
const DOT_RADIUS = 9;
const CONTENT_PADDING = 18;

export function drawTurnBanner(
  ctx: CanvasRenderingContext2D,
  view: TurnBannerView,
  palette: RenderPalette,
): void {
  const { text, dot, bg } = describe(view, palette);
  const bannerY = (BOARD_OUTER_OFFSET - BANNER_HEIGHT) / 2;

  drawBanner(
    ctx,
    { text, bg, dot },
    palette,
    {
      canvasWidth: CANVAS_SIZE,
      y: bannerY,
      height: BANNER_HEIGHT,
      radius: BANNER_RADIUS,
      font: BANNER_FONT,
      dotRadius: DOT_RADIUS,
      contentPadding: CONTENT_PADDING,
      // The board turns by geometry, not by flipping the canvas, so the banner
      // is always upright as drawn. (Passing the viewer's 180 here turned it
      // upside down for whoever played from the far side.)
      rotation: 0,
    },
  );
}

interface Resolved {
  readonly text: string;
  readonly dot: DotColors | null;
  readonly bg: string;
}

function describe(view: TurnBannerView, palette: RenderPalette): Resolved {
  switch (view.kind) {
    case 'turn':
      return {
        text: view.player === 'white' ? 'White to move' : 'Black to move',
        dot: pieceDotColors(view.player, palette),
        bg: palette.bannerBg,
      };
    case 'win':
      return {
        text: view.winner === 'white' ? 'White wins' : 'Black wins',
        dot: pieceDotColors(view.winner, palette),
        bg: palette.bannerWinBg,
      };
    case 'draw':
      return {
        text: 'Draw',
        dot: null,
        bg: palette.bannerDrawBg,
      };
  }
}

function pieceDotColors(player: Color, palette: RenderPalette): DotColors {
  const isWhite = player === 'white';
  return {
    base: isWhite ? palette.whitePieceBase : palette.blackPieceBase,
    highlight: isWhite ? palette.whitePieceHighlight : palette.blackPieceHighlight,
    shadow: isWhite ? palette.whitePieceShadow : palette.blackPieceShadow,
    border: isWhite ? palette.whitePieceBorder : palette.blackPieceBorder,
  };
}
